import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Removes outer border/radius — for full-viewport app layout. */
  flat?: boolean;
  /** Fills parent height — for AppShellShowcase / design-system embed. */
  showcase?: boolean;
}

export function AppShell({
  flat = false,
  showcase = false,
  className,
  children,
  ...props
}: AppShellProps) {
  return (
    <div
      className={cn('shell', flat && 'shell--flat', showcase && 'shell--showcase', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface AppShellRailProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function AppShellRail({ className, children, ...props }: AppShellRailProps) {
  return (
    <aside className={cn('shell-rail', className)} {...props}>
      {children}
    </aside>
  );
}

export interface AppShellRailBrandProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function AppShellRailBrand({ className, children, ...props }: AppShellRailBrandProps) {
  return (
    <div className={cn('rail-brand', className)} {...props}>
      {children}
    </div>
  );
}

export interface AppShellRailButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  children: ReactNode;
}

export function AppShellRailButton({
  active = false,
  className,
  type = 'button',
  children,
  ...props
}: AppShellRailButtonProps) {
  return (
    <button type={type} className={cn('rail-btn', active && 'is-active', className)} {...props}>
      {children}
    </button>
  );
}

export type AppShellRailSpacerProps = HTMLAttributes<HTMLDivElement>;

export function AppShellRailSpacer({ className, ...props }: AppShellRailSpacerProps) {
  return <div className={cn('rail-spacer', className)} {...props} />;
}

export interface AppShellTopProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function AppShellTop({ className, children, ...props }: AppShellTopProps) {
  return (
    <header className={cn('shell-top', className)} {...props}>
      {children}
    </header>
  );
}

export interface AppShellTopActionsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Groups topbar actions (search margin + trailing buttons). */
export function AppShellTopActions({ className, children, ...props }: AppShellTopActionsProps) {
  return (
    <div className={cn('shell-top-actions', className)} {...props}>
      {children}
    </div>
  );
}

export interface AppShellBodyProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function AppShellBody({ className, children, ...props }: AppShellBodyProps) {
  return (
    <main className={cn('shell-body', className)} {...props}>
      {children}
    </main>
  );
}
